import {
  AnchorButton,
  Button,
  Classes,
  FormGroup,
  IconName,
  InputGroup,
  Intent,
  MaybeElement,
} from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';
import { Dialog, Tooltip } from 'vet-bones/components';

import { TEST_IDS } from 'src/constants/testIds';
import * as copy from 'src/templates/copy';

export type PromptDialogProps = {
  additionalInputs?: JSX.Element;
  cancelButtonIntent?: Intent;
  cancelButtonOutlined?: boolean;
  cancelButtonText?: string;
  dialogClassName?: string;
  getErrorTextForValue?: (value: string) => string;
  handleSubmit: (value: string) => void;
  handleCancel: () => void;
  icon?: IconName | MaybeElement;
  initialValue?: string;
  isOpen: boolean;
  message?: React.ReactNode;
  placeholder?: string;
  submitButtonIntent?: Intent;
  submitButtonOutlined?: boolean;
  submitButtonText?: string;
  title?: string;
};

export const PromptDialog: React.VFC<PromptDialogProps> = ({
  additionalInputs,
  cancelButtonIntent = Intent.PRIMARY,
  cancelButtonOutlined = true,
  cancelButtonText = copy.components.defaults.prompt.cancel,
  dialogClassName,
  getErrorTextForValue = () => '',
  handleSubmit,
  handleCancel,
  icon,
  initialValue = '',
  isOpen,
  message,
  placeholder = '',
  submitButtonIntent = Intent.PRIMARY,
  submitButtonOutlined,
  submitButtonText = copy.components.defaults.prompt.submit,
  title,
}) => {
  const [value, setValue] = React.useState(initialValue);
  const [hasTyped, setHasTyped] = React.useState(false);
  const errorText = getErrorTextForValue(value);
  const disabled = Boolean(errorText);
  const dangerText = (hasTyped && errorText) || '';

  const onSubmit = (evt: React.FormEvent) => {
    evt.preventDefault();
    if (!disabled) {
      handleSubmit(value);
    }
  };

  React.useEffect(() => {
    if (!isOpen) {
      setValue('');
      setHasTyped(false);
    } else if (initialValue) {
      setValue(initialValue);
      setHasTyped(true);
    }
  }, [isOpen, setValue, initialValue]);

  return (
    <Dialog
      autoFocus={false}
      canEscapeKeyClose={false}
      canOutsideClickClose={false}
      className={classNames('sd-dialog-prompt', dialogClassName)}
      closeIconTestId={TEST_IDS.dialog.closeIcon}
      enforceFocus={false}
      icon={icon}
      isOpen={isOpen}
      lazy={false}
      onClose={handleCancel}
      title={title}
    >
      <div className={Classes.DIALOG_BODY}>
        <form onSubmit={onSubmit}>
          <FormGroup label={message} labelFor="sd-prompt-dialog-value">
            <Tooltip
              content={dangerText || ''}
              disabled={!dangerText}
              fill
              intent={Intent.DANGER}
            >
              <InputGroup
                autoComplete="off"
                autoFocus
                data-testid={TEST_IDS.dialog.prompt.valueField}
                fill
                id="sd-prompt-dialog-value"
                intent={dangerText ? Intent.DANGER : Intent.NONE}
                name="value"
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                  setHasTyped(true);
                  setValue(evt.target.value);
                }}
                placeholder={placeholder}
                value={value}
              />
            </Tooltip>
          </FormGroup>
          {additionalInputs}
          <input hidden type="submit" />
        </form>
      </div>

      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <Button
            data-testid={TEST_IDS.dialog.prompt.cancelButton}
            intent={cancelButtonIntent}
            onClick={handleCancel}
            outlined={cancelButtonOutlined}
            text={cancelButtonText}
          />
          <Tooltip
            content={errorText || ''}
            disabled={!errorText}
            intent={dangerText ? Intent.DANGER : Intent.NONE}
          >
            <AnchorButton
              data-testid={TEST_IDS.dialog.prompt.submitButton}
              disabled={disabled}
              intent={submitButtonIntent}
              onClick={onSubmit}
              outlined={submitButtonOutlined}
              text={submitButtonText}
            />
          </Tooltip>
        </div>
      </div>
    </Dialog>
  );
};
